/**
 * ISSUE-1380. The Scores search's copy, its debounce, and the link targets
 * for a result row. sports-api POST /search/teams-players does the matching
 * and the ordering; this file only decides when to ask and where a row goes.
 */
import type { typePlayerSearch } from "../../routeState/playerSearch";

export const SCORE_SEARCH_TITLE = "Search";
export const SCORE_SEARCH_BACK_LABEL = "Back to Scores";
export const SCORE_SEARCH_INPUT_LABEL = "Search teams and players";
export const SCORE_SEARCH_PLACEHOLDER = "Team or player name";
export const SCORE_SEARCH_HINT = "Type at least 2 letters.";
export const SCORE_SEARCH_TEAMS_HEADING = "Teams";
export const SCORE_SEARCH_PLAYERS_HEADING = "Players";
export const SCORE_SEARCH_LOADING_LABEL = "Searching";
export const SCORE_SEARCH_FAILED_LINE = "We could not search right now.";
export const SCORE_SEARCH_ENTRY_LABEL = "Search teams and players";

/** The server answers nothing shorter, so the client never asks. */
export const SCORE_SEARCH_MIN_LENGTH = 2;
/** How long typing has to pause before the URL, and so the read, follows. */
export const SCORE_SEARCH_DEBOUNCE_MS = 350;

/** The text the read is asked for: trimmed, or null when too short to ask. */
export function searchableQuery(raw: string | undefined): string | null {
  const trimmed = (raw ?? "").trim().replace(/\s+/g, " ");
  return trimmed.length >= SCORE_SEARCH_MIN_LENGTH ? trimmed : null;
}

export function scoreSearchEmptyLine(query: string): string {
  return `No teams or players match "${query}".`;
}

export type typeScoreSearchTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
  leagueName: string;
  logoUrl?: string | null;
};

export type typeScoreSearchPlayer = {
  playerId: string;
  name: string;
  position?: string | null;
  headshotUrl?: string | null;
  teamId: string;
  teamName: string;
  teamAbbreviation: string;
  leagueName: string;
};

/** "NBA" for a team row's second line. */
export function teamResultMeta(team: typeScoreSearchTeam): string {
  return team.leagueName;
}

/**
 * "Los Angeles Lakers · F · NBA" for a player row's second line. The league
 * closes the line so two players with one name, say a football and a
 * basketball player at Alabama, never read as the same row (QA round 1).
 */
export function playerResultMeta(player: typeScoreSearchPlayer): string {
  const position = player.position?.trim() ?? "";
  const league = player.leagueName.trim();
  return [player.teamName, position, league]
    .filter((part) => part !== "")
    .join(" · ");
}

/** The player page's params, the same set a roster row sends. */
export function playerResultSearch(
  player: typeScoreSearchPlayer,
): typePlayerSearch {
  const position = player.position?.trim() ?? "";
  return {
    playerId: player.playerId,
    name: player.name,
    teamId: player.teamId,
    teamName: player.teamName,
    teamAbbreviation: player.teamAbbreviation,
    ...(position === "" ? {} : { position }),
  };
}
